import { Check, Plus, SealCheck } from "@phosphor-icons/react"
import { LinkButton } from "@tokenterminal/core/ui/button/LinkButton"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import Image from "next/image"
import { Fragment } from "react"
import { twMerge } from "tailwind-merge"
import { BASE_PATH } from "@/constants/app.mjs"
import Container from "../../common/Container"
import Layout from "../../common/Layout"
import MarketingHeading from "../../common/MarketingHeading"
import AppPartnerGrid from "./AppPartnerGrid"
import ContactTheTeamCTA from "./ContactTheTeamCTA"
import * as content from "./content"
import FeaturedCaseStudies from "./FeaturedCaseStudies"

const gradientOverlayCss = "absolute inset-0 bg-gradient-to-b from-transparent to-bg-default"

const heroImageCss = "w-full h-full object-cover object-bottom aspect-[2106/772]"

const headingCss = "text-[46px] lg:text-[70px] leading-[0.95em]  text-center"

const listingTypes = [
  {
    id: "basic",
    badgeText: "Basic Listings",
    description:
      "Get your project indexed in our data pipeline. We track your core onchain activity and provide an initial dataset to monitor key metrics – with the option to expand into full standardized coverage.",
    metrics: content.appListingMetrics.basic.metrics,
    numberMore:
      content.appListingMetrics.basic.total - content.appListingMetrics.basic.metrics.length,
    cta: (
      <LinkButton
        className="w-full mt-4"
        size="large"
        href="https://tokenterminal.com/explorer/listings"
      >
        Start your Listing
      </LinkButton>
    ),
  },
  {
    id: "standardized",
    badgeText: "Standardized Listings",
    description:
      "We take ownership of your data and reporting. Our research team works with you to define, standardize, and maintain financial and usage metrics – enabling you to understand your performance, benchmark against peers, and present consistent data to the market.",
    metrics: content.appListingMetrics.standardized.metrics,
    numberMore:
      content.appListingMetrics.standardized.total -
      content.appListingMetrics.standardized.metrics.length,
    cta: (
      <LinkButton className="w-full mt-4" size="large" href="/contact">
        Contact our team
      </LinkButton>
    ),
  },
] as const

const ListingTypeBadge = ({ type }: { type: "basic" | "standardized" }) => {
  return (
    <div
      className={twMerge(
        "flex items-center gap-2",
        type === "standardized" ? "standardized" : null,
      )}
    >
      {type === "basic" ? null : (
        <Icon as={SealCheck} weight="fill" width={20} className="text-cyan-500" />
      )}
      <MarketingHeading level={2} className="text-xl">
        {type === "basic" ? "Basic Listings" : "Standardized Listings"}
      </MarketingHeading>
    </div>
  )
}

const DataPartnershipPage = () => {
  return (
    <Layout className="theme-dark bg-bg-default text-fg-default">
      <div
        className="flex flex-col gap-2 justify-start"
        style={{
          position: "relative",
          width: "100%",
          gap: 100,
          textAlign: "left",
        }}
      >
        <div
          className={twMerge(
            "flex items-start justify-center gap-2 ",
            "absolute left-0 top-[-100px] h-[50vh] w-full overflow-hidden",
          )}
        >
          <Image
            src={`${BASE_PATH}/static/images/marketing/app-partnership/app-partnership-hero.png`}
            alt="Data Partnership Hero"
            width={2106}
            height={772}
            className={[heroImageCss, "animate-fade-in-up"].join(" ")}
          />
          <div className={gradientOverlayCss} />
        </div>
        <Container
          className="flex flex-col gap-8 items-center"
          style={{
            paddingTop: "150px",
            position: "relative",
            zIndex: 2,
            animationDelay: "0.25s",
            width: "100%",
            textAlign: "center",
          }}
        >
          <div className="text-base font-medium text-fg-secondary">For apps and RWA issuers</div>
          <MarketingHeading level={1} className={headingCss}>
            Make your data comparable, credible,
            <br />
            and globally visible
          </MarketingHeading>
          <div className="text-sm text-fg-secondary max-w-[600px] leading-[1.5em]">
            We transform your raw onchain data into standardized metrics, help you benchmark against
            peers, and distribute it to leading investors and platforms.
          </div>
        </Container>
        <Container className="flex flex-col gap-8 items-center" style={{ zIndex: 2 }}>
          <div className="max-w-[900px] mx-auto">
            <div className="flex items-stretch gap-12 flex-col md:flex-row">
              {listingTypes.map((listingType, index) => (
                <Fragment key={listingType.id}>
                  <div className="flex-1">
                    <div className="flex flex-col gap-5 h-full">
                      <ListingTypeBadge type={listingType.id} />
                      <div className="text-sm text-fg-secondary leading-[20px] mb-4 flex-1">
                        {listingType.description}
                      </div>
                      <div className="text-sm text-fg-secondary font-medium">Supported Metrics</div>
                      <div className="flex flex-col gap-3">
                        {listingType.metrics.map((metric) => (
                          <div key={metric} className="flex items-center justify-start gap-2">
                            <Icon as={Check} />
                            <div className="text-base text-fg-default">{metric}</div>
                          </div>
                        ))}
                        <div className="flex items-center justify-start gap-2 text-fg-secondary">
                          <Icon as={Plus} />
                          <div className="text-base">
                            {listingType.numberMore} more
                            {listingType.id === "standardized" ? " (and custom metrics)" : ""}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-start gap-2">{listingType.cta}</div>
                    </div>
                  </div>
                  {index === 0 && (
                    <div
                      style={{
                        width: "1px",
                        background: "rgba(255,255,255,.1)",
                      }}
                    />
                  )}
                </Fragment>
              ))}
            </div>
          </div>
        </Container>
        <Container>
          <AppPartnerGrid />
        </Container>
        <Container>
          <FeaturedCaseStudies />
        </Container>
        <div className="bg-gradient-to-b from-bg-default to-bg-surface">
          <Container className="pb-[40px]">
            <ContactTheTeamCTA />
          </Container>
        </div>
      </div>
    </Layout>
  )
}

export default DataPartnershipPage
